import { AlertTriangle, CheckCircle2, Lightbulb, Sparkles } from 'lucide-react';
import type { z } from 'zod';
import type { infoCard } from '../../shared/registry/info-card.ts';
import { cn } from '../lib/cn.ts';

type Props = z.output<typeof infoCard.params>;

const TONES = {
  neutral: { icon: Sparkles, ring: 'ring-fg/10', accent: 'text-accent', dot: 'bg-accent/80' },
  tip: { icon: Lightbulb, ring: 'ring-warn/20', accent: 'text-warn', dot: 'bg-warn/80' },
  warning: { icon: AlertTriangle, ring: 'ring-bad/25', accent: 'text-bad', dot: 'bg-bad/80' },
  success: { icon: CheckCircle2, ring: 'ring-good/25', accent: 'text-good', dot: 'bg-good/80' },
} as const;

export function InfoCard({ title, subtitle, bullets, tone }: Props) {
  const t = TONES[tone];
  const Icon = t.icon;
  return (
    <article className={cn('card ring-1', t.ring)}>
      <header className="flex items-start gap-3">
        <span className={cn('mt-0.5 rounded-xl bg-fg/5 p-2', t.accent)}>
          <Icon className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold leading-snug text-fg">{title}</h3>
          {subtitle ? <p className="mt-0.5 text-sm text-subtle">{subtitle}</p> : null}
        </div>
      </header>
      <ul className="mt-4 space-y-2.5">
        {bullets.map((b) => (
          <li key={b} className="flex gap-3 text-[15px] leading-relaxed text-fg/85">
            <span className={cn('mt-2.5 size-1.5 shrink-0 rounded-full', t.dot)} aria-hidden />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}
